import React, { useCallback, useEffect, useMemo } from 'react';
import { CellRendererProps, FlatList, StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation, useScrollToTop } from '@react-navigation/native';

import { EventInfo } from '@/api/events/types/types';
import { AppNavBar, NAV_BAR_HEIGHT } from '@/components/AppNavBar';
import { TABS_HEIGHT } from '@/components/LeftSideTabBar';
import { SizedBox } from '@/components/SizedBox';
import { BOTTOM_TABS_HEIGHT } from '@/components/TabBar';
import {
    LEAGUE_NAVIGATOR_HEIGHT,
    LIST_PADDING_BOTTOM,
} from '@/components/league-navigator/utils/league-navigator-utils';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { PlayerWithTeam, PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { createPlayerEventKey } from '@/feature/betslip-pickem/utils/markets-cache-store-utils';
import {
    CardSegmentTitle,
    CardSegmentTitleType,
} from '@/feature/fantasy-event-sheets/components/ufc-events/CardSegmentTitle';
import { LEAGUES_LIST_HEIGHT, LobbyLeagues } from '@/feature/lobby/components/LobbyLeagues';
import { usePickTypeBadgesEducation } from '@/feature/lobby/components/PickTypeBadgesEducationProvider';
import { PickemNavigationLaunchpad } from '@/feature/lobby/components/PickemNavigationLaunchpad';
import { useRaceToPlaceStore } from '@/feature/lobby/components/race-to-place/hooks/use-race-to-place-store';
import { useFantasyLobbyContent } from '@/feature/lobby/hooks/use-fantasy-lobby-content';
import { lobbyId, useLobbySectionSelection } from '@/feature/lobby/hooks/use-lobby-section-selection';
import { LobbyProductSwitcherChipsHandle } from '@/feature/product-transition/components/LobbyProductSwitcherChipsHandle';
import {
    LOBBY_HANDLE_HEIGHT,
    LobbyWrapper,
    productSwitcherStyles,
    useLobbyListStyles,
} from '@/feature/product-transition/components/LobbyWrapper';
import { PRODUCT_CHIPS_HEIGHT } from '@/feature/product-transition/components/ProductSwitcherChips';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { WHATS_NEW_LOBBY_CARD_TOTAL_HEIGHT, WhatsNewLobbyCard } from '@/feature/whats-new/components/WhatsNewLobbyCard';
import { useWhatsNewVisibility } from '@/feature/whats-new/hooks/use-whats-new-visibility';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useLeagueNavigationEnabled } from '@/hooks/use-league-navigation-enabled';
import { usePrevious } from '@/hooks/use-previous';
import { useProductSwitchModal } from '@/hooks/use-product-switch';
import { useShowOneTime } from '@/hooks/use-show-one-time';
import { PickemLobbyTabNavigationProp } from '@/navigation/pickem/types';
import { IndividualVersusEvent, League } from '@/types/api.generated';
import { ProductDocumentDataBodyLobbycomponentsSliceItem } from '@/types/prismic.generated';
import { SocialFeedEntry } from '@/types/socialFeed.generated';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';

import { usePlayerFiltersActions, usePlayerFiltersScrollControls } from '../hooks/use-player-filters';
import { StickyWrapper, useStickyScrollHandler } from '../hooks/use-sticky-translation';
import { Filters } from '../utils/filters';
import { DEFAULT_LOBBY_ORDERING, LobbyComponent } from '../utils/lobbySections';
import { StickyCellRendererComponent } from './sticky-item/StickyCellRendererComponent';

export const LAUNCHPAD_SCROLLED_BOTTOM_OFFSET = 12;
export const LOBBY_PICK_BAR_HEIGHT_WITH_MARGIN = 112;

const LOBBY_HEADER_PADDING_BOTTOM = 6;
const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 60 }; //at least 60% of the item own height is visible in the viewport

type LobbyListContent =
    | LobbyLeagueSection
    | CardSegmentTitleType // Main Card, Prelims, Early Prelims - for UFC league
    | IndividualVersusEvent //For UFC rendering players grouped by fight on the fights tab
    | SocialFeedEntry // For Lineups tab containing social cards
    | ProductDocumentDataBodyLobbycomponentsSliceItem //Lobby prismic configured components (Favorites, Top 10, Shared lineups, etc)
    | PlayerWithTeamAndEvent; //

export type LobbyLeagueSection =
    | 'productSwitcher'
    | 'handle'
    | 'logo'
    | 'whatsNew'
    | 'leagues'
    | 'eventsCards'
    | 'filters'
    | keyof typeof LobbyComponent;

export const LobbyLeagueComponents = {
    Lobby: ['handle', 'logo', 'whatsNew', 'leagues'],
    League: ['handle', 'logo', 'whatsNew', 'leagues', 'eventsCards', 'filters'],
    ExpandedEvent: ['filters'],
} as const;

/**
 * This is the list of the elements on the lobby/leagues screen
 * If selectedTile is `lobby` we display the CommonComponents + Lobby components:
 *  - ['logo', 'search', 'leagues', 'lobbyContent']
 * If selectedTile is a League we display the CommonComponents + League components + List of players/projections
 * - ['logo', 'search', 'leagues', 'eventsCards', 'filters'] + players's row/tile
 * If selectedTile is the UFC League we display the CommonComponents + League components + Card segments + List of players grouped by fight
 * - ['logo', 'search', 'leagues', 'eventsCards', 'filters'] + CardSegmentTitleType | IndividualVersusEvent
 */
export const LobbyList = ({ league }: { league: League }) => {
    const { listRef, filtersStickPosition, listScrollOffset: scrollOffset } = usePlayerFiltersScrollControls();
    //TODO: remove ts-ignore when upgrading react navigation to v7
    // @ts-ignore
    useScrollToTop(listRef);

    const configuredComponents = useJurisdictionStore(
        state => state.jurisdictionSettings?.productConfig?.lobbyComponents?.items ?? DEFAULT_LOBBY_ORDERING
    );
    const setIsR2PCarouselInViewPort = useRaceToPlaceStore(state => state.actions.setIsR2PCarouselInViewPort);

    const lobbyComponents = useMemo(() => {
        return configuredComponents.filter(c => c.enabled).filter(Boolean);
    }, [configuredComponents]);

    const selectedTile = useLobbySectionSelection(state => state.selectedTile);
    const { hasUnseen } = useWhatsNewVisibility();
    const { listMinHeight } = useElementsDimensions(hasUnseen);
    const leagueNavigationEnabled = useLeagueNavigationEnabled();

    const prevSelectedTile = usePrevious(selectedTile);

    const isLobby = selectedTile === 'lobby';

    const setSelectedTile = useLobbySectionSelection(state => state.actions.setSelectedTile);
    const { resetAllFilters } = usePlayerFiltersActions();
    const navigation = useNavigation<PickemLobbyTabNavigationProp>();

    const handleStickPosition = useSharedValue(0);
    const leaguesStickPosition = useSharedValue(0);

    const { onScroll, scrollDirection, scrollVelocity } = useStickyScrollHandler(scrollOffset);
    const { lobbyHandleHeight } = useLobbyListStyles();
    const launchpadHeight = leagueNavigationEnabled ? LEAGUE_NAVIGATOR_HEIGHT : LEAGUES_LIST_HEIGHT;

    const {
        sortedPlayers,
        sortedLobbyPlayers,
        sortedGroupedUFCPlayers,
        socialFeedEntries,
        renderLobbyPlayerItem,
        renderLobbyComponent,
        renderPlayerItem,
        renderUFCFightRow,
        renderEventCards,
        renderFilters,
        renderLeagueFooter,
        renderSocialFeedItem,
        filterType,
    } = useFantasyLobbyContent();

    useEffect(() => {
        if (scrollOffset.value > leaguesStickPosition.value && prevSelectedTile !== selectedTile) {
            listRef?.current?.scrollToOffset({ animated: false, offset: leaguesStickPosition.value });
        }
    }, [leaguesStickPosition, listRef, prevSelectedTile, scrollOffset, selectedTile]);

    useEffect(() => {
        if (!league) {
            navigation.setParams({ league: lobbyId });
        }
    }, [league, navigation]);

    useEffect(() => {
        if (league) {
            setSelectedTile(league);
            resetAllFilters();
        } else {
            setSelectedTile(lobbyId);
        }
    }, [league, resetAllFilters, setSelectedTile]);

    const insets = useSafeAreaInsets();

    // Show the Pick Type badges education sheet once, when the user first enters the lobby.
    const { show, handleInteraction } = useShowOneTime('Pickem_Lobby_PickTypeBadgesEducation');
    const { showPickTypeBadgesEducationModal } = usePickTypeBadgesEducation();

    useEffect(() => {
        if (!show) {
            return;
        }

        showPickTypeBadgesEducationModal();
        handleInteraction();
    }, [show, showPickTypeBadgesEducationModal, handleInteraction]);

    const listToRender = useMemo<readonly LobbyListContent[]>(() => {
        if (isLobby) {
            return [...LobbyLeagueComponents.Lobby, ...lobbyComponents, ...sortedLobbyPlayers];
        }
        if (selectedTile === League.Ufc && filterType === Filters.Position) {
            return [...LobbyLeagueComponents.League, ...sortedGroupedUFCPlayers];
        }
        if (filterType === Filters.SocialLineups) {
            return [...LobbyLeagueComponents.League, ...socialFeedEntries];
        }
        return [...LobbyLeagueComponents.League, ...sortedPlayers];
    }, [
        isLobby,
        selectedTile,
        filterType,
        sortedPlayers,
        lobbyComponents,
        sortedLobbyPlayers,
        sortedGroupedUFCPlayers,
        socialFeedEntries,
    ]);

    const renderListItem = useCallback(
        ({ item, index }: { item: LobbyListContent; index: number }) => {
            if (typeof item === 'string') {
                if (item === 'handle') {
                    return (
                        <LobbyProductSwitcherChipsHandle
                            handleStickPosition={handleStickPosition}
                            scrollOffset={scrollOffset}
                            scrollDirection={scrollDirection}
                            scrollVelocity={scrollVelocity}
                        />
                    );
                }
                if (item === 'logo') {
                    return <AppNavBar />;
                }
                if (item === 'whatsNew') {
                    return <WhatsNewLobbyCard />;
                }
                if (item === 'leagues') {
                    return (
                        <StickyWrapper
                            scrollDirection={scrollDirection}
                            scrollOffset={scrollOffset}
                            initialPosition={leaguesStickPosition}
                            scrollVelocity={scrollVelocity}
                        >
                            <Box backgroundColor={ds.colorToken.background.neutral.base}>
                                {leagueNavigationEnabled ? <PickemNavigationLaunchpad /> : <LobbyLeagues />}
                            </Box>
                        </StickyWrapper>
                    );
                }
                if (item === 'eventsCards') {
                    return renderEventCards();
                }
                if (item === 'filters') {
                    return (
                        <StickyWrapper
                            scrollDirection={scrollDirection}
                            scrollOffset={scrollOffset}
                            initialPosition={filtersStickPosition}
                            scrollVelocity={scrollVelocity}
                        >
                            <Box backgroundColor={ds.colorToken.background.neutral.base}>{renderFilters()}</Box>
                        </StickyWrapper>
                    );
                }
                console.warn('Unknown item type in LobbyList:', item);
                return null;
            }
            if (isLobbyComponentPrismicSlice(item)) {
                return <MaxWidthWrapper key={item.orderingkey}>{renderLobbyComponent(item)}</MaxWidthWrapper>;
            }
            if (isSocialFeedEntry(item)) {
                return renderSocialFeedItem({ item, index, ignoreIndexes: LobbyLeagueComponents.League.length });
            }
            if (isCardSegmentTitle(item)) {
                return (
                    <MaxWidthWrapper key={`card-segment-title-${index}`}>
                        <CardSegmentTitle
                            cardSegmentDetails={item}
                            // index 0 is used for the Projection/Fights tab filters, so first segment title will be at index 1
                            style={index === 1 ? styles.firstCardSegmentTitle : undefined}
                        />
                    </MaxWidthWrapper>
                );
            }

            if (isUfcEvent(item)) {
                return <MaxWidthWrapper key={item.id}>{renderUFCFightRow(item)}</MaxWidthWrapper>;
            }
            if (isLobby) {
                //For the lobby we render the lobby player item component and also pass the title so we can render it
                //above the first player in the lobby list
                const title = lobbyComponents.find(c => c.orderingkey === LobbyComponent.FeaturedPlayersList)?.header;
                return (
                    <MaxWidthWrapper key={item.id}>
                        {renderLobbyPlayerItem({ item, index, title: title || '' })}
                    </MaxWidthWrapper>
                );
            }
            return renderPlayerItem({ item, index, ignoreIndexes: LobbyLeagueComponents.League.length });
        },
        [
            isLobby,
            renderPlayerItem,
            handleStickPosition,
            scrollOffset,
            scrollDirection,
            scrollVelocity,
            leaguesStickPosition,
            leagueNavigationEnabled,
            renderEventCards,
            filtersStickPosition,
            renderFilters,
            renderLobbyComponent,
            renderSocialFeedItem,
            renderUFCFightRow,
            lobbyComponents,
            renderLobbyPlayerItem,
        ]
    );

    // using a custom cell renderer component to make sure
    // the sticky components are rendered above the other items
    const CellRendererComponent = useCallback(
        ({ children, style, ...props }: CellRendererProps<LobbyListContent>) => {
            const isFilters = props.cellKey === 'filters';
            const isLeagues = props.cellKey === 'leagues';
            const isHandle = props.cellKey === 'handle';

            const adjustedStyle = {
                style,
                // set custom zIndex for the sticky components
                // this is needed to make sure the sticky components are rendered above the other items
                zIndex: cellKeyToZIndex[props.cellKey] || undefined,
                // on web we use "position: sticky" which needs the "top" property to be set
                // and represents the distance from the top of the list where it becomes sticky
                top: !isWeb ? 0 : isFilters ? launchpadHeight - TABS_HEIGHT : 0,
            };
            return (
                <StickyCellRendererComponent
                    {...props}
                    shouldBeSticky={isFilters || isLeagues}
                    style={adjustedStyle}
                    onLayout={event => {
                        // The launchpad has a different offset when is scrolled at the top
                        // (different margin top when is scrolled vs not scrolled)
                        // this offset needs to be added to each item bellow

                        // projection/position filters should stick below the launchpad
                        // this means that the stick position should be the initial y position of the filters
                        // minus the height of the launchpad (launchpadHeight)
                        // minus the top inset
                        // plus the height of the LeftSideTabBar (TABS_HEIGHT)
                        // plus the offset of the launchpad
                        // plus the height of the handle if product switching is available
                        if (isFilters) {
                            filtersStickPosition.value =
                                event.nativeEvent.layout.y -
                                insets.top -
                                launchpadHeight +
                                TABS_HEIGHT +
                                LAUNCHPAD_SCROLLED_BOTTOM_OFFSET +
                                LIST_PADDING_BOTTOM;
                        }
                        // leagues should stick at top
                        // this means that the stick position should be the initial y position of the leagues
                        // minus the top inset
                        // plus the offset of the launchpad
                        // plus the height of the handle if product switching is available
                        if (isLeagues) {
                            leaguesStickPosition.value =
                                event.nativeEvent.layout.y - insets.top + LAUNCHPAD_SCROLLED_BOTTOM_OFFSET;
                        }

                        // handle should stick at top
                        // this means that the stick position should be the initial y position of the handle
                        // plus the height of the product chips
                        // plus the height of the handle (if product switching is available)
                        if (isHandle) {
                            handleStickPosition.value =
                                event.nativeEvent.layout.y + PRODUCT_CHIPS_HEIGHT + lobbyHandleHeight;
                        }
                        props.onLayout?.(event);
                    }}
                >
                    {children}
                </StickyCellRendererComponent>
            );
        },
        [
            launchpadHeight,
            filtersStickPosition,
            insets.top,
            leaguesStickPosition,
            handleStickPosition,
            lobbyHandleHeight,
        ]
    );

    const playersTabSpace = filterType === Filters.Position ? 6 : 0;

    const renderFooter = useCallback(() => {
        return <Box style={{ marginBottom: 84 + playersTabSpace }}>{renderLeagueFooter()}</Box>;
    }, [renderLeagueFooter, playersTabSpace]);

    const contentContainerStyle = useMemo(() => {
        return [
            {
                minHeight: listMinHeight,
            },
            productSwitcherStyles.lobbyListContentContainerStyle,
        ];
    }, [listMinHeight]);

    // Called when the viewability of rows changes
    const onViewableItemsChanged = useCallback(
        ({ viewableItems }: { viewableItems: any[] }) => {
            const visibleComponents = viewableItems.reduce<Record<string, boolean>>((acc, item) => {
                const orderingKey = item.item?.orderingkey;
                if (orderingKey) {
                    acc[orderingKey] = true;
                }
                return acc;
            }, {});
            const isR2pComponentVisible = !!visibleComponents?.[LobbyComponent.RaceToPlace];
            setIsR2PCarouselInViewPort(isR2pComponentVisible);
        },
        [setIsR2PCarouselInViewPort]
    );

    return (
        <LobbyWrapper>
            <AnimatedFlatList
                keyExtractor={keyExtractor}
                onScroll={onScroll}
                onViewableItemsChanged={onViewableItemsChanged}
                viewabilityConfig={VIEWABILITY_CONFIG}
                data={listToRender}
                contentContainerStyle={contentContainerStyle}
                windowSize={isWeb ? undefined : 5}
                // on web, the FlatList doesn't load the next page when scrolling
                initialNumToRender={isWeb ? listToRender.length : 20}
                maxToRenderPerBatch={10}
                ref={listRef}
                renderItem={renderListItem}
                CellRendererComponent={CellRendererComponent}
                ListFooterComponent={isLobby ? <SizedBox value={LOBBY_PICK_BAR_HEIGHT_WITH_MARGIN} /> : renderFooter}
                ListEmptyComponent={ListEmptyComponent}
                showsVerticalScrollIndicator={showScrollIndicator}
                // needed for sticky components to work on Android
                removeClippedSubviews={false}
            />
        </LobbyWrapper>
    );
};

export const ListEmptyComponent = () => {
    const { height } = useWindowDimensions();
    return <SizedBox value={height} />;
};

const keyExtractor = (item: LobbyListContent, index: number) => {
    if (typeof item === 'string') {
        return `${item}`;
    } else if (isLobbyComponentPrismicSlice(item)) {
        return `LobbyItem-${item.orderingkey}`;
    } else if (isSocialFeedEntry(item)) {
        return `social-feed-${item.socialFeedEntryId}-${index}`;
    } else if (isCardSegmentTitle(item)) {
        return `CardSegmentTitle-${index}`;
    } else if (isUfcEvent(item)) {
        return `event-${item.id}-${index}`;
    } else {
        return `player-${createPlayerEventKey(item.id, item.event.id)}-${index}`;
    }
};

/**
 * it returns the listMinHeight which is essential to avoid jumping of the list when it's length is recalculated
 * it returns the leaguesOffset which is the offset of the league cards list
 */
const useElementsDimensions = (hasUnseen: boolean) => {
    const insets = useSafeAreaInsets();
    const { switchingAvailable } = useProductSwitchModal();
    const { height } = useWindowDimensions();

    const lobbyScreenHeight = height - insets.bottom - BOTTOM_TABS_HEIGHT;
    const whatsNewCardHeight = hasUnseen ? WHATS_NEW_LOBBY_CARD_TOTAL_HEIGHT : 0;

    const singleProductListMinHeight =
        lobbyScreenHeight +
        NAV_BAR_HEIGHT +
        LOBBY_HEADER_PADDING_BOTTOM +
        LAUNCHPAD_SCROLLED_BOTTOM_OFFSET +
        whatsNewCardHeight;

    const allProductsListMinHeight = singleProductListMinHeight + LOBBY_HANDLE_HEIGHT + PRODUCT_CHIPS_HEIGHT;

    return { listMinHeight: switchingAvailable ? allProductsListMinHeight : singleProductListMinHeight };
};

const isLobbyComponentPrismicSlice = (
    item: LobbyListContent
): item is ProductDocumentDataBodyLobbycomponentsSliceItem => typeof item === 'object' && 'orderingkey' in item;

const isSocialFeedEntry = (item: LobbyListContent): item is SocialFeedEntry =>
    typeof item === 'object' && 'socialFeedEntryId' in item;

const isUfcEvent = (item: EventInfo | PlayerWithTeam): item is EventInfo => item.__typename === 'IndividualVersusEvent';

const isCardSegmentTitle = (cardSegmentDetails: {
    __typename?: string;
}): cardSegmentDetails is CardSegmentTitleType => {
    return cardSegmentDetails.__typename === 'CardSegmentTitle';
};

const AnimatedFlatList = Animated.createAnimatedComponent(FlatList<LobbyListContent>);

const cellKeyToZIndex: { [key: string]: number } = {
    filters: 101,
    leagues: 102,
    handle: 103,
} as const;

const styles = StyleSheet.create({
    firstCardSegmentTitle: {
        paddingTop: 0,
    },
});
